import { useState } from 'react'
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Container,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  Input,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  Snackbar,
  Alert,
  TextField,
  Typography,
} from '@material-ui/core'
import { Form, Formik } from 'formik'
import { useDispatch, useSelector } from 'react-redux'
import { AddCourse } from 'src/redux/actions'
import { generateUniqueFileName, getFilePreview, ImageRatioSize } from 'src/util/helpers'
import { uploadToS3 } from 'src/util/uploadToS3'
import * as Yup from 'yup'

import { StatusList, VisibilityList } from '../../util/constants'
import DropdownMenu from '../helperComponents/DropdownMenu'
import LoadingButton from '../helperComponents/LoadingButton'

const AddEditLanguage = ({ selectedRow, toggleDrawer, refetch, products, rzpPlans }) => {
  const dispatch = useDispatch()
  const [errorMessage, setErrorMessage] = useState('')
  const [showError, setShowError] = useState(false)
  const [successMessage, setSuccessMessage] = useState('')
  const [showSuccess, setShowSuccess] = useState(false)

  const { courseCategories, courseDifficultyLevel } = useSelector(
    store => store.mainReducer
  )

  let initialValues = {
    name: '',
    description: '',
    langIcon: '',
    video: '',
    category: 'Language',
    difficultyLevel: 'All levels',
    duration: 0,
    product: '',
    rzpProduct: [],
  }

  if (selectedRow) {
    initialValues = {
      name: selectedRow.name,
      description: selectedRow.description,
      langIcon: selectedRow.langIcon,
      video: selectedRow.video,
      category: selectedRow.category,
      difficultyLevel: selectedRow.difficultyLevel,
      duration: selectedRow.duration,
      sortOrder: (selectedRow && selectedRow?.sortOrder) || '',
      id: selectedRow.id,
      isActive:
        selectedRow && selectedRow.isActive === undefined
          ? true
          : selectedRow && selectedRow?.isActive,
      isPublished:
        selectedRow && selectedRow.isPublished === undefined
          ? false
          : selectedRow && selectedRow?.isPublished,
      product: selectedRow?.product || '',
      rzpProduct: selectedRow?.rzpProduct || [],
    }
  }

  const validationSchema = Yup.object().shape(
    {
      name: Yup.string()
        .min(2, 'Minimum 2 characters required')
        .max(75, 'Maximum 75 characters are allowed')
        .required('Required'),
      sortOrder: Yup.number(),
      product: Yup.string().when('rzpProduct', {
        is: (rzpProduct) => !rzpProduct || rzpProduct.length === 0,
        then: Yup.string().required('Either Stripe or at least one Razorpay plan must be selected'),
        otherwise: Yup.string(), // ✅ if rzpProduct filled, product is optional
      }),
      rzpProduct: Yup.array().when('product', {
        is: (product) => !product || product.length === 0,
        then: Yup.array().min(1, 'Either Stripe or at least one Razorpay plan must be selected'),
        otherwise: Yup.array(), // ✅ if product filled, rzpProduct is optional
      }),
    },
    [['product', 'rzpProduct']]
  )

  const handleCancel = () => {
    toggleDrawer(false)
  }

  const onChangeLanguageIcon = ({ target: { name, files } }, setFieldValue) => {
    if (files) {
      let file = files[0]
      file.fieldLabel = name
      setFieldValue('langIcon', file)
    }
  }

  const showErrorMsg = (msg) => {
    setErrorMessage(msg || 'Something went wrong. Please try again.')
    setShowError(true)
  }

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const reqData = selectedRow._id
        ? { ...values, _id: selectedRow._id }
        : { ...values }

      if (!reqData.id) {
        delete reqData.id
      }

      for (let key in reqData) {
        if (reqData[key] instanceof File) {
          let file = reqData[key]
          const uniqueName = generateUniqueFileName(file)
          const uniqueFile = new File([file], uniqueName, { type: file.type })
          await uploadToS3(uniqueFile)
          reqData[key] = uniqueName
        }
      }

      const response = await dispatch(AddCourse(reqData))

      if (response?.error) {
        let serverMessage = 'Something went wrong. Please try again.'

        if (response?.payload?.message) {
          serverMessage = response.payload.message
        } else if (response?.payload?.errors) {
          serverMessage = Object.values(response.payload.errors)
            .map(e => e.message)
            .join(', ')
        } else if (typeof response?.error === 'string') {
          serverMessage = response.error
        } else if (response?.error?.message) {
          serverMessage = response.error.message
        }

        showErrorMsg(serverMessage)
        return
      }

      refetch()
      setSuccessMessage(selectedRow?._id ? 'Language updated successfully' : 'Language added successfully')
      setShowSuccess(true)
      setTimeout(() => toggleDrawer(false), 1500)

    } catch (error) {
      showErrorMsg(error?.message)
    } finally {
      setSubmitting(false)
    }
  }

  const formatProductsData = products => {
    return [...products].map(prod => ({ key: prod.id, value: prod.name }))
  }

  // multiselect we need { id, name } shape
  const formattedRzpPlans = [...(rzpPlans || [])].map(prod => ({
    id: prod.id,
    name: prod?.item?.name || prod.id,
  }))

  return (
    <>
      {/* Error Snackbar */}
      <Snackbar
        open={showError}
        autoHideDuration={6000}
        onClose={() => setShowError(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setShowError(false)} severity="error" variant="filled" sx={{ width: '100%' }}>
          {errorMessage}
        </Alert>
      </Snackbar>

      {/* Success Snackbar */}
      <Snackbar
        open={showSuccess}
        autoHideDuration={1500}
        onClose={() => setShowSuccess(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setShowSuccess(false)} severity="success" variant="filled" sx={{ width: '100%' }}>
          {successMessage}
        </Alert>
      </Snackbar>

      <Box sx={{ m: 1 }}>
        <Container sx={{ width: '100%' }}>
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({
              errors,
              touched,
              values,
              handleBlur,
              handleChange,
              setFieldValue,
              isSubmitting,
            }) => (
              <Form>
                <Grid container spacing={2}>
                  {/* Left Column */}
                  <Grid item xs={12} md={6}>
                    <TextField
                      error={Boolean(touched.name && errors.name)}
                      fullWidth
                      helperText={touched.name && errors.name}
                      label="Name"
                      margin="normal"
                      name="name"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      value={values.name}
                      variant="outlined"
                      required
                      size='small'
                    />

                    {/* Language Icon Upload */}
                    <Grid container spacing={1} alignItems="center" sx={{ minHeight: 120, paddingLeft: 1 }}>
                      <Grid item xs={12} sm={6}>
                        <FormControlLabel
                          label=""
                          htmlFor="langIcon"
                          control={
                            <>
                              <Input
                                style={{ display: 'none' }}
                                onChange={event => {
                                  handleChange(event)
                                  onChangeLanguageIcon(event, setFieldValue)
                                }}
                                name="langIcon"
                                inputProps={{ accept: 'image/*' }}
                                id="langIcon"
                                type="file"
                              />
                              <Button variant="contained" component="span" fullWidth sx={{ height: 50 }}>
                                Add Icon
                              </Button>
                            </>
                          }
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Box sx={{ width: 'full', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'end' }}>
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                              border: '1px dashed #ccc',
                              borderRadius: 2,
                              width: '100%',
                              height: 120,
                              maxWidth: 120,
                              overflow: 'hidden',
                              backgroundColor: '#fafafa',
                            }}
                          >
                            {values.langIcon ? (
                              <Box sx={{ maxWidth: '100%', maxHeight: '100%' }}>
                                {getFilePreview(values.langIcon)}
                              </Box>
                            ) : (
                              <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center' }}>
                                No Icon Selected
                              </Typography>
                            )}
                          </Box>
                          <ImageRatioSize ratio="1:1" minWidth="30px" size="200kb" />
                        </Box>
                      </Grid>
                    </Grid>

                    {/* Stripe Products — disabled when any Razorpay plan is selected */}
                    <FormControl error={Boolean(touched.product && errors.product)} fullWidth margin="normal">
                      <DropdownMenu
                        value={values?.product}
                        label="Stripe Products"
                        name="product"
                        isRequired={false}
                        onChange={handleChange}  // ✅ plain handleChange, no clearing
                        data={formatProductsData(products)}
                      />
                      <FormHelperText>{touched.product && errors.product}</FormHelperText>
                    </FormControl>

                    {/* ✅ Razorpay Plans — multi-select, disabled when Stripe is selected */}
                    <FormControl
                      error={Boolean(touched.rzpProduct && errors.rzpProduct)}
                      fullWidth
                      margin="normal"
                      // ✅ removed: disabled={!!values?.product}
                    >
                      <InputLabel id="rzpProduct-label">Razorpay Plans</InputLabel>
                      <Select
                        labelId="rzpProduct-label"
                        id="rzpProduct"
                        multiple
                        value={values.rzpProduct}
                        onChange={(e) => {
                          setFieldValue('rzpProduct', e.target.value)
                          // ✅ removed: clearing product on change
                        }}
                        input={<OutlinedInput label="Razorpay Plans" />}
                        renderValue={(selected) => (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {selected.map((id) => {
                              const plan = formattedRzpPlans.find(p => p.id === id)
                              return <Chip key={id} label={plan?.name || id} size="small" />
                            })}
                          </Box>
                        )}
                      >
                        {formattedRzpPlans.map((plan) => (
                          <MenuItem key={plan.id} value={plan.id}>
                            <Checkbox checked={values.rzpProduct.indexOf(plan.id) > -1} />
                            <ListItemText primary={plan.name} />
                          </MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>
                        {touched.rzpProduct && errors.rzpProduct}
                      </FormHelperText>
                    </FormControl>
                  </Grid>

                  {/* Right Column */}
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth margin="normal">
                      <DropdownMenu
                        value={values.isActive}
                        label="Status"
                        name="isActive"
                        isRequired={true}
                        onChange={handleChange}
                        data={StatusList}
                      />
                    </FormControl>

                    <FormControl fullWidth margin="normal">
                      <DropdownMenu
                        value={values.isPublished}
                        label="Visibility"
                        name="isPublished"
                        isRequired={true}
                        onChange={handleChange}
                        data={VisibilityList}
                      />
                    </FormControl>

                    <TextField
                      fullWidth
                      label="Sort Order"
                      margin="normal"
                      inputProps={{ min: 0 }}
                      name="sortOrder"
                      type="number"
                      defaultValue={0}
                      onChange={handleChange}
                      value={values?.sortOrder}
                      variant="outlined"
                      size="small"
                    />
                  </Grid>

                  {/* Action Buttons */}
                  <Grid item xs={12} md={6}>
                    <Button color="primary" onClick={handleCancel} fullWidth size="large" type="button" variant="outlined">
                      Cancel
                    </Button>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <LoadingButton
                      color="primary"
                      disabled={isSubmitting}
                      fullWidth
                      size="large"
                      type="submit"
                      variant="contained"
                      name="Save"
                    />
                  </Grid>
                </Grid>
              </Form>
            )}
          </Formik>
        </Container>
      </Box>
    </>
  )
}

export default AddEditLanguage